import { type FC, memo, useCallback, useEffect, useState } from 'react'

import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { Button } from '@/components/ui/button'
import {
  $fanLanguage,
  cancelTranslateTextsRequest,
  translateTextsFx,
} from '@/models/chattingUI/messages/translations'
import { $creatorId } from '@/models/creators'
import TranslationIcon from '@/modules/chatting/icons/translation.svg'
import { TrackedFeatures, trackFeatureUsage } from '@/utils/feature-engagement'

import { IconButton } from '../../components/icon-button'
import {
  EditorEventHandlerPluginKey,
  EditorEventHandlerPluginOrder,
} from '../message-editor/consts'
import { useEditor } from '../message-editor/use-editor'
import { languages } from './consts'

interface Props {
  onSend: (message: string) => Promise<void>
  isSending: boolean
  isSendDisabled: boolean
}

export const TranslationHint: FC<Props> = memo(
  ({ onSend, isSending, isSendDisabled }) => {
    const [isVisible, setIsVisible] = useState(false)
    const language = useUnit($fanLanguage)!
    const creatorId = useUnit($creatorId)
    const [translation, setTranslation] = useState('')
    const isTranslating = useUnit(translateTextsFx.pending)

    const {
      text,
      editor,
      registerEnterHandlerPlugin,
      unregisterEnterHandlerPlugin,
    } = useEditor()

    const translate = useCallback(async () => {
      const translations = await translateTextsFx({ texts: [text], language })

      setTranslation(translations?.[0] ?? '')
    }, [language, text])

    const hideHint = useCallback(() => {
      setIsVisible(false)
    }, [])

    const sendTranslation = () => {
      trackFeatureUsage(TrackedFeatures.AiTranslatorMessaging, creatorId)
      onSend(translation)
      setTranslation('')
      hideHint()
    }

    useEffect(() => {
      setTranslation('')
    }, [language])

    useEffect(() => {
      if (!text || text !== translation) {
        cancelTranslateTextsRequest()
        setTranslation('')
        setIsVisible(text.trim().length > 0)
      }
      // no need for translation. we only track text changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text])

    useEffect(() => {
      if (isVisible) {
        registerEnterHandlerPlugin({
          key: EditorEventHandlerPluginKey.TRANSLATION_HINT,
          order: EditorEventHandlerPluginOrder.TRANSLATION_HINT,
          enterHandler: (event) => {
            event.preventDefault()
            if (translation) {
              editor?.commands.setContent(translation, true)
              hideHint()
              trackFeatureUsage(
                TrackedFeatures.AiTranslatorMessaging,
                creatorId,
              )
            } else if (!isTranslating) {
              translate()
            }
          },
        })
      } else {
        unregisterEnterHandlerPlugin()
      }
    }, [
      registerEnterHandlerPlugin,
      unregisterEnterHandlerPlugin,
      translate,
      hideHint,
      translation,
      isVisible,
      editor?.commands,
      isTranslating,
      creatorId,
    ])

    const languageLabel = languages.find((lang) => lang.code === language)?.name

    if (!isVisible) {
      return null
    }

    return (
      <div className="bg-card left-0 right-0 px-6 py-4 space-y-4 rounded-lg border">
        <div className="flex items-center justify-between gap-4">
          <div className="text-secondary-300 flex items-center gap-2 text-base">
            <TranslationIcon className="shrink-0" />
            {languageLabel}: to translate press enter or button on the right
          </div>
          <IconButton
            icon={CloseIcon}
            onClick={hideHint}
          />
        </div>
        <div className="flex items-start gap-4 justify-between">
          <div className="bg-primary-100 dark:bg-primary-900 px-3 py-2.5 rounded-lg text-base w-max">
            {translation || text}
          </div>
          {translation ? (
            <Button
              onClick={sendTranslation}
              disabled={isSendDisabled || isSending}
              variant="outline"
            >
              Send
            </Button>
          ) : (
            <Button
              variant="outline"
              className="shrink-0"
              onClick={translate}
              disabled={isTranslating}
            >
              Translate
            </Button>
          )}
        </div>
      </div>
    )
  },
)

TranslationHint.displayName = 'TranslationHint'
